import React, { useState, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Tabs,
  Tab,
  Paper,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DownloadIcon from '@mui/icons-material/Download';
import VerifiedIcon from '@mui/icons-material/Verified';
import PlanningCalendar from '../components/calendar/PlanningCalendar';
import LiisaOnCallImport from '../components/calendar/LiisaOnCallImport';
import OnCallSchedule from '../components/calendar/OnCallSchedule';
import OnCallForm from '../components/calendar/OnCallForm';
import AutoPlanningDialog from '../components/calendar/AutoPlanningDialog';
import BulkDeleteDialog from '../components/calendar/BulkDeleteDialog';
import ConflictCheckDialog from '../components/calendar/ConflictCheckDialog';
import AgentDialogueChat from '../components/calendar/AgentDialogueChat'; // ✅ Préconnexion WebSocket
import PageTitle from '../components/layout/PageTitle';
import { useTeamPermissions } from '../hooks/useTeamPermissions';
import { useTechnicians } from '../contexts/TechniciansContext';
import { useSkills } from '../contexts/SkillsContext';
import { useOnCalls } from '../contexts/OnCallsContext';
import { format, startOfYear, addWeeks, addDays, isWithinInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import { holidaysService } from '../services/holidaysService';
import ConflictDetectionService from '../services/conflictDetectionService';
import AIConflictAnalysisService from '../services/aiConflictAnalysis';
import { useAbsences } from '../contexts/AbsencesContext';
import { useLocation } from 'react-router-dom';

const CalendarPage = () => {
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState(0);
  const [openForm, setOpenForm] = useState(false);
  const [openAutoPlanning, setOpenAutoPlanning] = useState(false);
  const [openBulkDelete, setOpenBulkDelete] = useState(false);
  const [openConflictCheck, setOpenConflictCheck] = useState(false);
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [selectedOnCall, setSelectedOnCall] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [periodFilter, setPeriodFilter] = useState('all'); // 'all', 'month', 'quarter', 'semester'
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-12
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3)); // 1-4
  const [selectedSemester, setSelectedSemester] = useState(new Date().getMonth() < 6 ? 1 : 2); // 1-2
  const [conflictResults, setConflictResults] = useState(null);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);
  const [complianceRefreshTrigger, setComplianceRefreshTrigger] = useState(0);
  const { canAdd, canDelete, canEdit, canView } = useTeamPermissions();
  const { technicians: allTechnicians } = useTechnicians();
  const { skills } = useSkills();
  const { onCalls } = useOnCalls();
  const { absences } = useAbsences();

  // Gestion des paramètres de navigation depuis le dialog de conflit
  React.useEffect(() => {
    if (location.state?.highlightOnCallId || location.state?.focusWeek) {
      // Basculer vers l'onglet Planning
      setCurrentTab(1);
      
      // Si une année spécifique est fournie, l'appliquer
      if (location.state?.selectedYear) {
        setSelectedYear(location.state.selectedYear);
      }
      
      // Forcer un refresh pour éviter le cache
      setRefreshKey(prev => prev + 1);
      
      // Nettoyer le state après un délai pour permettre la navigation
      setTimeout(() => {
        window.history.replaceState({}, '', window.location.pathname);
      }, 100);
    }
  }, [location.state?.timestamp, location.state?.highlightOnCallId]); // Dépendre du timestamp pour forcer la mise à jour

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const handlePeriodFilterChange = (filter) => {
    setPeriodFilter(filter);
    if (filter === 'all') {
      // Réinitialiser tous les filtres aux valeurs par défaut
      setSelectedMonth(new Date().getMonth() + 1);
      setSelectedQuarter(Math.ceil((new Date().getMonth() + 1) / 3));
      setSelectedSemester(new Date().getMonth() < 6 ? 1 : 2);
    }
  };

  const handleMonthChange = (event) => {
    const value = event.target.value;
    setSelectedMonth(value);
    if (value === '') {
      setPeriodFilter('all');
    } else {
      setPeriodFilter('month');
      // Mettre à jour automatiquement le trimestre et semestre correspondants
      const quarter = Math.ceil(value / 3);
      const semester = value <= 6 ? 1 : 2;
      setSelectedQuarter(quarter);
      setSelectedSemester(semester);
    }
  };

  const handleQuarterChange = (event) => {
    const value = event.target.value;
    setSelectedQuarter(value);
    if (value === '') {
      setPeriodFilter('all');
    } else {
      setPeriodFilter('quarter');
      // Mettre à jour automatiquement le semestre correspondant
      const semester = value <= 2 ? 1 : 2;
      setSelectedSemester(semester);
      // Réinitialiser le mois sélectionné car on filtre par trimestre
      setSelectedMonth('');
    }
  };

  const handleSemesterChange = (event) => {
    const value = event.target.value;
    setSelectedSemester(value);
    if (value === '') {
      setPeriodFilter('all');
    } else {
      setPeriodFilter('semester');
      // Réinitialiser le mois et trimestre sélectionnés car on filtre par semestre
      setSelectedMonth('');
      setSelectedQuarter('');
    }
  };

  // Générer la liste des années disponibles (N-2 à N+2)
  const getAvailableYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 2; year <= currentYear + 2; year++) {
      years.push(year);
    }
    return years;
  };

  // Nom des mois en français
  const getMonthName = (monthNumber) => {
    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return months[monthNumber - 1];
  };

  // Obtenir les mois disponibles selon le filtre actuel
  const getAvailableMonths = () => {
    if (periodFilter === 'quarter' && selectedQuarter) {
      // Si un trimestre est sélectionné, ne montrer que les mois de ce trimestre
      const startMonth = (selectedQuarter - 1) * 3 + 1;
      return [startMonth, startMonth + 1, startMonth + 2];
    } else if (periodFilter === 'semester' && selectedSemester) {
      // Si un semestre est sélectionné, ne montrer que les mois de ce semestre
      return selectedSemester === 1 ? [1, 2, 3, 4, 5, 6] : [7, 8, 9, 10, 11, 12];
    } else {
      // Sinon, montrer tous les mois
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    }
  };

  // Obtenir les trimestres disponibles selon le filtre actuel
  const getAvailableQuarters = () => {
    if (periodFilter === 'semester' && selectedSemester) {
      // Si un semestre est sélectionné, ne montrer que les trimestres de ce semestre
      return selectedSemester === 1 ? [1, 2] : [3, 4];
    } else {
      // Sinon, montrer tous les trimestres
      return [1, 2, 3, 4];
    }
  };

  // Obtenir les semestres disponibles selon le filtre actuel
  const getAvailableSemesters = () => {
    if (periodFilter === 'quarter' && selectedQuarter) {
      // Si un trimestre est sélectionné, ne montrer que le semestre correspondant
      return selectedQuarter <= 2 ? [1] : [2];
    } else {
      // Sinon, montrer tous les semestres
      return [1, 2];
    }
  };

  const handleOpenForm = () => {
    setSelectedOnCall(null);
    setOpenForm(true);
  };

  const handleOpenAutoPlanning = () => {
    setOpenAutoPlanning(true);
  };

  const handleOpenBulkDelete = () => {
    setOpenBulkDelete(true);
  };

  const handleOpenConflictCheck = async () => {
    setOpenConflictCheck(true);
    setIsCheckingConflicts(true);
    setConflictResults(null);

    try {
      // Convertir les techniciens en map pour un accès plus facile
      const techniciansMap = allTechnicians.reduce((acc, tech) => {
        acc[tech.id] = tech;
        return acc;
      }, {});

      // Charger les jours fériés pour l'année sélectionnée
      const yearHolidays = await holidaysService.getHolidays(selectedYear);

      // Pour la vérification des périodes de repos, inclure les astreintes de l'année précédente et suivante
      // car les repos peuvent s'étaler sur plusieurs années (fin 2025 -> début 2026)
      const extendedOnCalls = onCalls.filter(onCall => {
        const onCallYear = new Date(onCall.startDate).getFullYear();
        // Inclure l'année sélectionnée et les années adjacentes pour les vérifications de repos
        return onCallYear >= selectedYear - 1 && onCallYear <= selectedYear + 1;
      });

      // Lancer la vérification de conformité avec les astreintes étendues
      const results = await ConflictDetectionService.checkCompliance(
        selectedYear,
        extendedOnCalls, // Utiliser extendedOnCalls au lieu de onCalls
        techniciansMap,
        skills,
        absences,
        yearHolidays
      );

      // Enrichir avec l'analyse IA
      const enhancedResults = await AIConflictAnalysisService.enhanceAnalysis(results, {
        technicians: techniciansMap,
        skills,
        selectedYear
      });

      setConflictResults(enhancedResults);
    } catch (error) {
      console.error('Erreur lors de la vérification de conformité:', error);
      setConflictResults({
        totalChecks: 0,
        conflicts: [{
          type: 'SYSTEM_ERROR',
          severity: 'critical',
          message: 'Erreur lors de la vérification',
          details: error.message
        }],
        warnings: [],
        suggestions: [],
        summary: {
          criticalErrors: 1,
          warnings: 0,
          suggestions: 0
        }
      });
    } finally {
      setIsCheckingConflicts(false);
    }
  };

  const handleEditOnCall = (onCall) => {
    setSelectedOnCall(onCall);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedOnCall(null);
    setRefreshKey(prev => prev + 1);
  };

  const handleCloseAutoPlanning = () => {
    setOpenAutoPlanning(false);
  };

  const handleCloseBulkDelete = () => {
    setOpenBulkDelete(false);
  };

  const handleCloseConflictCheck = () => {
    setOpenConflictCheck(false);
    setConflictResults(null);
  };

  const handlePlanningGenerated = (assignments) => {
    // Rafraîchir le calendrier après génération du planning
    setRefreshKey(prev => prev + 1);
    setOpenAutoPlanning(false);
  };

  const handleBulkDeleted = (deletedCount) => {
    // Rafraîchir le calendrier après suppression en masse
    setRefreshKey(prev => prev + 1);
    setOpenBulkDelete(false);
    // Optionnel : afficher une notification de succès
    console.log(`${deletedCount} astreinte(s) supprimée(s)`);
  };

  const handleDataChange = () => {
    setRefreshKey(prev => prev + 1);
    // Déclencher un refresh de la conformité
    setComplianceRefreshTrigger(prev => prev + 1);
  };

  const handleOpenExportDialog = () => {
    setExportYear(selectedYear);
    setOpenExportDialog(true);
  };

  const handleCloseExportDialog = () => {
    setOpenExportDialog(false);
  };

  const handleConfirmExport = () => {
    exportPlanningXLSX(exportYear);
    setOpenExportDialog(false);
  };

  const exportPlanningXLSX = useCallback(async (yearToExport = null) => {
    try {
      // Maps util
      const techniciansMap = allTechnicians.reduce((acc, t) => { acc[t.id] = t; return acc; }, {});
      const skillsById = skills.reduce((acc, s) => { acc[String(s.id)] = s; return acc; }, {});
      const skillsByName = skills.reduce((acc, s) => { acc[String(s.name).toLowerCase()] = s; return acc; }, {});
      const fallbackMap = { '1':'ALCATEL', '2':'AVAYA', '3':'CISCO', '4':'MITEL', '5':'LANSECU', '6':'COE' };
      const normalizeSkillToken = (token) => {
        if (token == null) return '';
        if (typeof token === 'object') {
          const raw = token.skillId ?? token.id;
          const key = raw != null ? String(raw) : '';
          return skillsById[key]?.name || fallbackMap[key] || key;
        }
        if (typeof token === 'number') {
          const key = String(token);
          return skillsById[key]?.name || fallbackMap[key] || key;
        }
        const t = String(token).trim();
        if (/^\d+$/.test(t)) {
          const key = String(parseInt(t, 10));
          return skillsById[key]?.name || fallbackMap[key] || key;
        }
        const byName = skillsByName[t.toLowerCase()];
        return byName?.name || t;
      };

      // Sheet 2: Planning (colonnes = compétences visibles)
      // Générer 52 semaines (vendredi->jeudi) de l'année sélectionnée
      const exportYear = yearToExport || (currentTab === 1 ? selectedYear : new Date().getFullYear());
      let firstFriday = startOfYear(new Date(exportYear, 0, 1));
      while (firstFriday.getDay() !== 5) {
        firstFriday = addDays(firstFriday, 1);
      }
      const yearHolidays = await holidaysService.getHolidays(exportYear).catch(() => []);
      const getHolidayName = (d) => holidaysService.getHolidayName ? holidaysService.getHolidayName(d, yearHolidays) : '';

      const header = ['Semestre', 'Période', 'Semaine', ...skills.map((s) => s.name), 'Jours fériés'];
      const planningRows = [];
      const listAggregated = [];
      for (let week = 0; week < 52; week++) {
        const weekStart = addWeeks(firstFriday, week);
        const weekEnd = addDays(weekStart, 6);
        const semester = weekStart.getMonth() < 6 ? 'S1' : 'S2';
        const assignments = {};
        onCalls.forEach((onCall) => {
          const s = new Date(onCall.startDate);
          const e = new Date(onCall.endDate);
          if (
            isWithinInterval(weekStart, { start: s, end: e }) ||
            isWithinInterval(weekEnd, { start: s, end: e }) ||
            isWithinInterval(s, { start: weekStart, end: weekEnd })
          ) {
            (onCall.skills || []).forEach((sk) => {
              const skillId = typeof sk === 'object' ? (sk.skillId ?? sk.id) : sk;
              assignments[skillId] = onCall.technicianId;
            });
          }
        });
        // Agréger pour l'onglet Astreintes: regrouper par technicien les compétences affectées cette semaine
        const byTech = {};
        Object.entries(assignments).forEach(([skillId, techId]) => {
          if (!techId) return;
          const sId = parseInt(skillId, 10);
          if (!byTech[techId]) byTech[techId] = new Set();
          byTech[techId].add(sId);
        });
        Object.entries(byTech).forEach(([techId, skillSet]) => {
          listAggregated.push({ start: weekStart, end: weekEnd, techId: parseInt(techId, 10), skillIds: Array.from(skillSet) });
        });

        const row = [];
        row.push(semester);
        row.push(`${format(weekStart, 'dd/MM', { locale: fr })} au ${format(weekEnd, 'dd/MM', { locale: fr })}`);
        row.push(`S${week + 1}`);
        skills.forEach((skill) => {
          const techId = assignments[skill.id];
          if (techId && techniciansMap[techId]) {
            row.push(`${techniciansMap[techId].firstName} ${techniciansMap[techId].lastName}`);
          } else {
            row.push('');
          }
        });
        const holidaysText = Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i))
          .map((d) => {
            const name = getHolidayName(d);
            return name ? `${format(d, 'dd/MM', { locale: fr })} ${name}` : '';
          })
          .filter(Boolean)
          .join(', ');
        row.push(holidaysText);
        planningRows.push(row);
      }
      const wsPlanning = XLSX.utils.aoa_to_sheet([header, ...planningRows]);

      // Sheet 1: Astreintes (liste) construite depuis le même agrégat
      listAggregated.sort((a, b) => a.start - b.start);
      const listRows = listAggregated.map((item) => {
        const uniqueNames = Array.from(new Set((item.skillIds || []).map((id) => {
          const key = String(id);
          return skillsById[key]?.name || fallbackMap[key] || key;
        })));
        return {
          'Date début': format(item.start, 'dd/MM/yyyy', { locale: fr }),
          'Date fin': format(item.end, 'dd/MM/yyyy', { locale: fr }),
          'Technicien': techniciansMap[item.techId] ? `${techniciansMap[item.techId].firstName} ${techniciansMap[item.techId].lastName}` : item.techId,
          'Compétences couvertes': uniqueNames.join(', ')
        };
      });
      const wsList = XLSX.utils.json_to_sheet(listRows);

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, wsList, 'Astreintes');
      XLSX.utils.book_append_sheet(wb, wsPlanning, 'Planning');
      const fileName = `planning-astreintes-${format(new Date(), 'yyyyMMdd-HHmm')}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (e) {
      console.error('❌ Export XLSX (page Planning) échoué:', e);
    }
  }, [allTechnicians, skills, onCalls, currentTab, selectedYear]);

  return (
    <Box>
      <PageTitle
        title="Planning des Astreintes"
        actions={
          (canAdd('oncalls') || canDelete('oncalls') || canEdit('oncalls')) && (
            <Stack direction="row" spacing={2}>
              {canDelete('oncalls') && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteSweepIcon />}
                  onClick={handleOpenBulkDelete}
                >
                  Suppression en masse
                </Button>
              )}
              {canAdd('oncalls') && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<SmartToyIcon />}
                  onClick={handleOpenAutoPlanning}
                >
                  Planification IA
                </Button>
              )}
              {canAdd('oncalls') && (
                <LiisaOnCallImport onSuccess={() => setRefreshKey(k => k + 1)} />
              )}
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleOpenExportDialog}
              >
                Exporter XLSX
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<VerifiedIcon />}
                onClick={handleOpenConflictCheck}
                disabled={isCheckingConflicts}
              >
                {isCheckingConflicts ? 'Vérification...' : 'Vérifier la conformité'}
              </Button>
              {canAdd('oncalls') && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenForm}
                >
                  Nouvelle astreinte
                </Button>
              )}
            </Stack>
          )
        }
      />

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Calendrier" />
          {canView('oncalls') && <Tab label="Planning" />}
        </Tabs>
      </Paper>

      {/* Filtres de période pour l'onglet Planning */}
      {currentTab === 1 && canView('oncalls') && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 3 }}>
          {/* Sélecteur de mois */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="month-select-label" shrink>Mois</InputLabel>
            <Select
              labelId="month-select-label"
              value={periodFilter === 'month' ? selectedMonth : ''}
              label="Mois"
              onChange={handleMonthChange}
              displayEmpty
              notched
            >
              <MenuItem value="">
                <em>Tous les mois</em>
              </MenuItem>
              {getAvailableMonths().map((month) => (
                <MenuItem key={month} value={month}>
                  {getMonthName(month)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Sélecteur de trimestre */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="quarter-select-label" shrink>Trimestre</InputLabel>
            <Select
              labelId="quarter-select-label"
              value={periodFilter === 'quarter' ? selectedQuarter : ''}
              label="Trimestre"
              onChange={handleQuarterChange}
              displayEmpty
              notched
            >
              <MenuItem value="">
                <em>Tous les trimestres</em>
              </MenuItem>
              {getAvailableQuarters().map((quarter) => (
                <MenuItem key={quarter} value={quarter}>
                  T{quarter} ({quarter === 1 ? 'Jan-Mar' : quarter === 2 ? 'Avr-Jun' : quarter === 3 ? 'Jul-Sep' : 'Oct-Déc'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Sélecteur de semestre */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="semester-select-label" shrink>Semestre</InputLabel>
            <Select
              labelId="semester-select-label"
              value={periodFilter === 'semester' ? selectedSemester : ''}
              label="Semestre"
              onChange={handleSemesterChange}
              displayEmpty
              notched
            >
              <MenuItem value="">
                <em>Tous les semestres</em>
              </MenuItem>
              {getAvailableSemesters().map((semester) => (
                <MenuItem key={semester} value={semester}>
                  S{semester} ({semester === 1 ? 'Jan-Jun' : 'Jul-Déc'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Sélecteur d'année */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="year-select-label" shrink>Année</InputLabel>
            <Select
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              label="Année"
              onChange={handleYearChange}
              notched
            >
              {getAvailableYears().map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Bouton pour réinitialiser tous les filtres */}
          <Button
            variant="outlined"
            size="small"
            onClick={() => handlePeriodFilterChange('all')}
            sx={{ minHeight: 40 }}
          >
            Réinitialiser
          </Button>
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        {(currentTab === 0 || !canView('oncalls')) ? (
          <PlanningCalendar 
            refreshKey={refreshKey}
            onEditOnCall={canEdit('oncalls') ? handleEditOnCall : null}
            onDataChange={handleDataChange}
          />
        ) : (
          <OnCallSchedule 
            key={refreshKey} 
            selectedYear={selectedYear} 
            periodFilter={periodFilter}
            selectedMonth={selectedMonth}
            selectedQuarter={selectedQuarter}
            selectedSemester={selectedSemester}
            highlightOnCallId={location.state?.highlightOnCallId}
            focusWeek={location.state?.focusWeek}
            highlightWeek={location.state?.focusWeek} // Passer focusWeek comme highlightWeek aussi
            onEditOnCall={canEdit('oncalls') ? handleEditOnCall : null}
            complianceRefreshTrigger={complianceRefreshTrigger}
            onDataChange={handleDataChange}
          />
        )}
      </Box>

      {/* Formulaire de création/modification d'astreinte */}
      {(canAdd('oncalls') || canEdit('oncalls')) && (
        <Dialog
          open={openForm}
          onClose={handleCloseForm}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedOnCall ? 'Modifier l\'astreinte' : 'Nouvelle astreinte'}
          </DialogTitle>
          <DialogContent>
            <OnCallForm 
              onClose={handleCloseForm}
              onCall={selectedOnCall}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Dialogue de planification automatique */}
      {canAdd('oncalls') && (
        <AutoPlanningDialog
          open={openAutoPlanning}
          onClose={handleCloseAutoPlanning}
          onPlanningGenerated={handlePlanningGenerated}
        />
      )}

      {/* Dialogue de suppression en masse */}
      {canDelete('oncalls') && (
        <BulkDeleteDialog
          open={openBulkDelete}
          onClose={handleCloseBulkDelete}
          onDeleted={handleBulkDeleted}
        />
      )}

      {/* Dialogue de vérification de conformité */}
      <ConflictCheckDialog
        open={openConflictCheck}
        onClose={handleCloseConflictCheck}
        results={conflictResults}
        isLoading={isCheckingConflicts}
        selectedYear={selectedYear}
        technicians={allTechnicians.reduce((acc, tech) => { acc[tech.id] = tech; return acc; }, {})}
        skills={skills}
      />

      {/* Dialogue de sélection d'année pour l'export */}
      <Dialog
        open={openExportDialog}
        onClose={handleCloseExportDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Sélectionner l'année à exporter
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="export-year-label">Année</InputLabel>
            <Select
              labelId="export-year-label"
              value={exportYear}
              label="Année"
              onChange={(e) => setExportYear(e.target.value)}
            >
              {getAvailableYears().map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExportDialog}>
            Annuler
          </Button>
          <Button 
            onClick={handleConfirmExport} 
            variant="contained" 
            color="primary"
            startIcon={<DownloadIcon />}
          >
            Exporter
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ WebSocket pré-connecté pour le chat IA (toujours monté, invisible) */}
      <Box sx={{ display: 'none' }}>
        <AgentDialogueChat isActive={false} isVisible={false} />
      </Box>
    </Box>
  );
};

export default CalendarPage;
